import React, { useEffect, useState } from "react";
import { i18n } from "../../translate/i18n";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, Typography } from "@material-ui/core";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import { he } from "date-fns/locale";
import { useHistory } from "react-router-dom";

import ChannelIcon from "../ChannelIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * CustomerJourney
 *
 * Phase 3.11 — Customer Journey Visibility
 *
 * Displays a vertical timeline of the contact's ticket history.
 * Structure only in Phase 3.11 — no new backend endpoint required
 * (uses existing GET /tickets filtered by contactId).
 *
 * Props:
 *   contactId: number
 *   companyId: number
 */

const STATUS_COLORS = {
  open:    "#25d366",
  pending: "#f59e0b",
  closed:  "#94a3b8",
};

const STATUS_LABELS = {
  open:    "contactDrawerSections.statusOpen",
  pending: "contactDrawerSections.statusPending",
  closed:  "contactDrawerSections.statusClosed",
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: { display: "flex", flexDirection: "column", gap: 4 },
    empty: {
      fontSize: 11,
      color: isDark ? "rgba(230,247,236,0.4)" : "#94a3b8",
      textAlign: "center",
      padding: "8px 0",
    },
    timeline: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      paddingLeft: 12,
      borderLeft: isDark ? "2px solid rgba(37,211,102,0.2)" : "2px solid #d4edda",
    },
    item: {
      position: "relative",
      paddingLeft: 16,
      paddingBottom: 12,
      cursor: "pointer",
      "&:last-child": { paddingBottom: 0 },
    },
    dot: {
      position: "absolute",
      left: -7,
      top: 4,
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: "2px solid",
      background: isDark ? "#131614" : "#fff",
    },
    itemContent: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      padding: "6px 8px",
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.03)" : "#f6faf7",
      border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e8f0ec",
      "&:hover": {
        background: isDark ? "rgba(37,211,102,0.06)" : "rgba(37,211,102,0.05)",
      },
    },
    itemHeader: {
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    statusPill: {
      fontSize: 9,
      fontWeight: 700,
      padding: "1px 6px",
      borderRadius: 6,
      color: "#fff",
    },
    ticketId: {
      fontSize: 10,
      fontWeight: 600,
      color: isDark ? "rgba(230,247,236,0.55)" : "#94a3b8",
    },
    lastMessage: {
      fontSize: 10,
      color: isDark ? "rgba(230,247,236,0.45)" : "#5f6f6a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    date: {
      fontSize: 9,
      color: isDark ? "rgba(230,247,236,0.3)" : "#94a3b8",
    },
    stats: {
      display: "flex",
      gap: 12,
      marginBottom: 8,
      padding: "6px 0",
    },
    stat: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    statValue: {
      fontSize: 14,
      fontWeight: 700,
      color: isDark ? "#7df0a8" : "#128c7e",
    },
    statLabel: {
      fontSize: 9,
      color: isDark ? "rgba(230,247,236,0.45)" : "#5f6f6a",
    },
  };
});

const CustomerJourney = ({ contactId, companyId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    api
      .get("/tickets", {
        params: { contactId, companyId, pageNumber: 1 },
      })
      .then(({ data }) => {
        const list = data.tickets || data || [];
        // Sort newest first
        list.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setTickets(list.slice(0, 20));
      })
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [contactId, companyId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
        <CircularProgress size={18} />
      </div>
    );
  }

  if (tickets.length === 0) {
    return <Typography className={classes.empty}>{i18n.t("contactDrawerSections.noTicketHistory")}</Typography>;
  }

  // Summary stats
  const openCount   = tickets.filter((t) => t.status === "open").length;
  const closedCount = tickets.filter((t) => t.status === "closed").length;
  const channels    = [...new Set(tickets.map((t) => t.channel).filter(Boolean))];
  const firstTicket = tickets[tickets.length - 1];

  return (
    <div className={classes.root}>
      {/* Quick stats */}
      <div className={classes.stats}>
        <div className={classes.stat}>
          <span className={classes.statValue}>{tickets.length}</span>
          <span className={classes.statLabel}>{i18n.t("contactDrawerSections.totalTickets")}</span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statValue}>{openCount}</span>
          <span className={classes.statLabel}>{i18n.t("contactDrawerSections.openTickets")}</span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statValue}>{channels.length}</span>
          <span className={classes.statLabel}>{i18n.t("contactDrawerSections.channels")}</span>
        </div>
        {firstTicket && (
          <div className={classes.stat}>
            <span className={classes.statValue} style={{ fontSize: 10 }}>
              {format(parseISO(firstTicket.createdAt), "dd/MM/yy")}
            </span>
            <span className={classes.statLabel}>{i18n.t("contactDrawerSections.customerSince")}</span>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className={classes.timeline}>
        {tickets.map((ticket) => {
          const dotColor = STATUS_COLORS[ticket.status] || "#94a3b8";
          return (
            <div
              key={ticket.id}
              className={classes.item}
              onClick={() => { if (ticket.uuid) history.push(`/tickets/${ticket.uuid}`); }}
            >
              <div
                className={classes.dot}
                style={{ borderColor: dotColor, background: undefined }}
              />
              <div className={classes.itemContent}>
                <div className={classes.itemHeader}>
                  {ticket.channel && (
                    <ChannelIcon channel={ticket.channel} size={12} />
                  )}
                  <span
                    className={classes.statusPill}
                    style={{ background: dotColor }}
                  >
                    {STATUS_LABELS[ticket.status] ? i18n.t(STATUS_LABELS[ticket.status]) : ticket.status}
                  </span>
                  <span className={classes.ticketId}>#{ticket.id}</span>
                  <span className={classes.date} style={{ marginLeft: "auto" }}>
                    {ticket.updatedAt
                      ? formatDistanceToNow(parseISO(ticket.updatedAt), {
                          addSuffix: true,
                          locale: he,
                        })
                      : ""}
                  </span>
                </div>
                {ticket.lastMessage && (
                  <div className={classes.lastMessage}>{ticket.lastMessage}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerJourney;
